<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
      <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-help-circle</v-icon>
        Question Details
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Top Navigation Bar -->
        <div class="d-flex justify-end mb-4">
          <v-btn
            color="grey"
            dark
            rounded
            size="large"
            @click="$router.back()"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Back
          </v-btn>
        </div>

        <!-- Question Header -->
        <div class="text-center mb-6">
          <h2 class="question-title mb-2">{{ question.question }}</h2>
          <p class="question-category mb-3">Law Question in Regards to {{ question.category }}</p>
          <v-chip 
            :color="getStatusColor(question.status)" 
            size="large"
            class="status-chip"
          >
            {{ question.status }}
          </v-chip>
        </div>

        <!-- Client Details Section (only for completed questions or when viewing details) -->
        <v-row v-if="showClientDetails">
          <v-col cols="12">
            <h3 class="section-title mb-4">Client Information</h3>
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="question.clientName || 'N/A'"
              label="Client Name"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-account"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="question.clientSurname || 'N/A'"
              label="Client Surname"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-account"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="question.dateReceived || question.dateAdded || 'N/A'"
              label="Date Received"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-calendar"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="question.dateAnswered || 'Pending'"
              label="Date Answered"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-calendar-check"
              readonly
            />
          </v-col>
          
          <v-col cols="12" md="6">
            <v-text-field
              :model-value="question.questionRef || 'N/A'"
              label="Question Reference"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-identifier"
              readonly
            />
          </v-col>
        </v-row>

        <!-- Question Section -->
        <v-row>
          <v-col cols="12">
            <h3 class="section-title mb-4">Question</h3>
          </v-col>
          
          <v-col cols="12">
            <v-textarea
              :model-value="question.question"
              label="Question"
              variant="solo"
              rounded
              flat
              hide-details
              prepend-inner-icon="mdi-help-circle"
              rows="4"
              readonly
            />
          </v-col>
        </v-row>

        <!-- Response Section -->
        <v-row>
          <v-col cols="12">
            <h3 class="section-title mb-4">Response</h3>
          </v-col>
          
          <!-- Show existing response if completed -->
          <template v-if="question.status === 'completed' && existingResponse">
            <v-col cols="12">
              <v-text-field
                :model-value="existingResponse.heading"
                label="Response Heading"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-format-header-1"
                readonly
              />
            </v-col>
            
            <v-col cols="12">
              <v-textarea
                :model-value="existingResponse.body"
                label="Response Body"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-text"
                rows="6"
                readonly
              />
            </v-col>
            
            <v-col cols="12">
              <v-textarea
                :model-value="existingResponse.conclusion"
                label="Response Conclusion"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-format-list-bulleted"
                rows="3"
                readonly
              />
            </v-col>
          </template>
          
          <!-- Response Form for pending questions -->
          <template v-else-if="question.status !== 'completed'">
            <v-col cols="12">
              <v-textarea
                v-model="response.heading"
                label="Response Heading"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-format-header-1"
                rows="2"
                placeholder="Enter a brief heading..."
                :rules="[v => !!v || 'Response heading is required']"
              />
            </v-col>
            
            <v-col cols="12">
              <v-textarea
                v-model="response.body"
                label="Response Body"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-text"
                rows="8"
                placeholder="Enter detailed response..."
                :rules="[v => !!v || 'Response body is required']"
              />
            </v-col>
            
            <v-col cols="12">
              <v-textarea
                v-model="response.conclusion"
                label="Response Conclusion"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-format-list-bulleted"
                rows="3"
                placeholder="Enter conclusion..."
                :rules="[v => !!v || 'Response conclusion is required']"
              />
            </v-col>
            
            <v-col cols="12" class="text-center">
              <v-btn
                color="#002060"
                dark
                rounded
                size="large"
                :disabled="!isResponseValid || loading"
                :loading="loading"
                @click="submitResponse"
                class="mr-4"
              >
                <v-icon left>mdi-send</v-icon>
                {{ loading ? 'Submitting...' : 'Submit Response' }}
              </v-btn>
            </v-col>
          </template>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { doc, getDoc, updateDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import NotificationDialog from '../components/NotificationDialog.vue';

export default {
  name: 'QaDetails',
  components: {
    NotificationDialog
  },
  data() {
    return {
      valid: false,
      loading: false,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      question: {},
      response: {
        heading: '',
        body: '',
        conclusion: ''
      },
      existingResponse: null,
      showClientDetails: false
    };
  },
  computed: {
    isResponseValid() {
      return this.response.heading.trim() && 
             this.response.body.trim() && 
             this.response.conclusion.trim();
    }
  },
  methods: {
    async submitResponse() {
      if (!this.isResponseValid) return;
      
      this.loading = true;
      this.showNotification = false;
      
      try {
        // Save response to Firestore
        const responseDoc = await addDoc(collection(db, 'responses'), {
          questionId: this.question.id,
          heading: this.response.heading,
          body: this.response.body,
          conclusion: this.response.conclusion,
          createdAt: new Date().toISOString(),
          respondedBy: 'Current User' // Replace with actual user info
        });

        // Update question status to completed
        await updateDoc(doc(db, 'questions', this.question.id), {
          status: 'completed',
          respondedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });

        console.log('Response sent successfully for question ID', this.question.id);
        
        // Log question responded activity
        const currentUser = this.$store.getters.user;
        const currentUserId = currentUser?.uid;
        const currentUserName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown';
        const questionTitle = this.question.question?.substring(0, 50) + (this.question.question?.length > 50 ? '...' : '');
        
        await ActivityLogger.questionResponded(currentUserName, currentUserId, questionTitle, 'QA Details Page');
        
        // Send email notification to client
        await this.sendResponseEmail();
        
        this.loading = false;
        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = 'Response sent successfully and notification email sent!';
        this.showNotification = true;
        
        // Clear form after successful submission
        this.response = {
          heading: '',
          body: '',
          conclusion: ''
        };
        
        // Navigate back to QA list after delay
        setTimeout(() => {
          this.$router.push({ name: 'Qa' });
        }, 2000);
        
      } catch (error) {
        console.error('Error sending response:', error);
        this.loading = false;
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to send response. Please try again.';
        this.showNotification = true;
      }
    },
    
    async sendResponseEmail() {
      try {
        // Get the current date in a readable format
        const dateAnswered = new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        
        // Prepare email data
        const emailData = {
          to: this.question.clientEmail || this.question.assignedToEmail || 'client@example.com',
          clientName: this.question.clientName || 'Client',
          clientSurname: '', // Will be handled by backend
          questionText: this.question.question,
          responseHeading: this.response.heading,
          responseBody: this.response.body,
          responseConclusion: this.response.conclusion,
          questionRef: this.question.questionRef || 'QA-' + this.question.id,
          category: this.question.category,
          dateAnswered: dateAnswered,
          respondedBy: 'Current User' // Replace with actual user info
        };
        
        // Call the Cloud Function
        const response = await fetch('https://us-central1-dmm-law-user-portal.cloudfunctions.net/sendQuestionResponseEmail', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(emailData)
        });
        
        const result = await response.json();
        
        if (result.success) {
          console.log('Response notification email sent successfully:', result.messageId);
        } else {
          console.error('Failed to send response notification email:', result.error);
        }
        
      } catch (error) {
        console.error('Error sending response notification email:', error);
        // Don't fail the entire response process if email fails
      }
    },
    
    async loadQuestion() {
      const questionId = this.$route.params.id;
      const viewDetails = this.$route.query.viewDetails === 'true';
      
      try {
        const docRef = doc(db, 'questions', questionId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          this.question = { id: docSnap.id, ...docSnap.data() };
          
          // Log question viewed activity
          const currentUser = this.$store.getters.user;
          const currentUserId = currentUser?.uid;
          const currentUserName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown';
          const questionTitle = this.question.question?.substring(0, 50) + (this.question.question?.length > 50 ? '...' : '');
          
          await ActivityLogger.questionViewed(currentUserName, currentUserId, questionTitle, 'QA Details Page');
          
          // Show client details if viewing details or if question is completed
          this.showClientDetails = viewDetails || this.question.status === 'completed';
          
          // Load existing response if question is completed
          if (this.question.status === 'completed') {
            await this.loadExistingResponse(questionId);
          }
        } else {
          // Fall back to mock data if not found in Firestore
          this.loadMockQuestion(questionId);
        }
      } catch (error) {
        console.error('Error loading question:', error);
        this.loadMockQuestion(questionId);
      }
    },
    
    async loadExistingResponse(questionId) {
      try {
        // Query responses collection for this question
        const responsesQuery = query(
          collection(db, 'responses'), 
          where('questionId', '==', questionId)
        );
        const responsesSnapshot = await getDocs(responsesQuery);
        
        if (!responsesSnapshot.empty) {
          // Get the first (and should be only) response
          const responseDoc = responsesSnapshot.docs[0];
          this.existingResponse = { id: responseDoc.id, ...responseDoc.data() };
        }
      } catch (error) {
        console.error('Error loading existing response:', error);
      }
    },
    
    loadMockQuestion(questionId) {
      const mockQuestions = [
        {
          id: 'q1',
          category: 'marriage',
          question: 'What are the grounds for annulment in South African law?',
          assignedTo: 'DMM Law',
          status: 'pending',
          dateAdded: '06/18/2025',
          clientName: 'John',
          clientSurname: 'Smith',
          clientEmail: 'john.smith@example.com',
          dateReceived: '06/18/2025',
          dateAnswered: null,
          questionRef: 'QA-001',
          createdAt: new Date().toISOString()
        },
        {
          id: 'q2',
          category: 'marriage',
          question: 'How to register a customary marriage?',
          assignedTo: 'Jane Doe',
          status: 'in_progress',
          dateAdded: '06/17/2025',
          clientName: 'Sarah',
          clientSurname: 'Johnson',
          clientEmail: 'sarah.johnson@example.com',
          dateReceived: '06/17/2025',
          dateAnswered: null,
          questionRef: 'QA-002',
          createdAt: new Date().toISOString()
        },
        {
          id: 'q3',
          category: 'property',
          question: 'Rights to property on divorce?',
          assignedTo: 'DMM Law',
          status: 'completed',
          dateAdded: '06/16/2025',
          clientName: 'Mike',
          clientSurname: 'Wilson',
          clientEmail: 'mike.wilson@example.com',
          dateReceived: '06/16/2025',
          dateAnswered: '06/20/2025',
          questionRef: 'QA-003',
          createdAt: new Date().toISOString()
        },
        {
          id: 'q4',
          category: 'contract',
          question: 'What constitutes a valid employment contract?',
          assignedTo: 'Legal Team',
          status: 'completed',
          dateAdded: '06/15/2025',
          clientName: 'ABC',
          clientSurname: 'Company',
          clientEmail: 'hr@abc-company.com',
          dateReceived: '06/15/2025',
          dateAnswered: '06/19/2025',
          questionRef: 'QA-004',
          createdAt: new Date().toISOString()
        }
      ];
      
      this.question = mockQuestions.find(q => q.id == questionId) || mockQuestions[0];
      
      // Show client details for completed questions
      this.showClientDetails = this.question.status === 'completed';
      
      // Add mock response for completed questions
      if (this.question.status === 'completed') {
        this.existingResponse = {
          heading: 'Legal Response',
          body: 'This is a detailed legal response to the question.',
          conclusion: 'Based on the above analysis, the conclusion is...'
        };
      }
    },
    getStatusColor(status) {
      switch (status) {
        case 'pending':
          return 'warning';
        case 'in_progress':
          return 'info';
        case 'completed':
          return 'success';
        default:
          return 'info';
      }
    }
  },
  created() {
    this.loadQuestion();
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.question-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #002060;
  margin: 0;
}

.question-category {
  font-size: 1rem;
  color: #666;
  margin: 0;
  font-weight: 500;
}

.status-chip {
  font-weight: 600;
  font-size: 1rem;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style> 